import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DirectoryPicker, dirEmail } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'
import { ARCADE_THEMES } from '../arcade/themes'
import { ModelAccessSwitch } from '../components/ModelAccessSwitch'

interface AdminHolder {
  id: string
  name: string
  /**
   * True when this holder is a GROUP, not a person (D3).
   *
   * CHU-GPT-Admin holds the Admin role by group assignment, which is the
   * design. Without this the group rendered here as a row that looked like
   * a colleague, with a Revoke button that then issued the `/users/` Graph
   * path and failed.
   */
  isGroup?: boolean
}

/** Matches `GET /api/users/app-roles`. */
interface AppRoleAssignment {
  id: string
  principalId: string
  principalDisplayName?: string
  roleName: string
  isGroup: boolean
}

/** Matches one probe of `GET /api/diagnostics`. */
interface ProbeResult {
  id: string
  label: string
  status: 'ok' | 'warn' | 'error' | 'skipped'
  detail: string
  ms: number
}
interface DiagnosticsReport {
  probes: ProbeResult[]
  overall: 'ok' | 'warn' | 'error'
  ranAt: string
}

/** Matches `backend/src/clients/settings.ts`'s `LeaderboardSettings`. */
interface LeaderboardSettings {
  excludedUsers: string[]
  hideCosts: boolean
  /** Org-wide default arcade skin. Presentation only. */
  defaultTheme?: string
  /** How many active people a group must field to be played as a SIDE in a
   *  rivalry fixture and to move the shield. NOT an exposure control: every
   *  office and every team is shown at every value. */
  contestFloor?: number
  /** The AI usage policy gate's kill switch (Round 14, Unit 12). */
  policyGateEnforced: boolean
}

/** `GET /api/settings/policy-gate/impact`. */
interface PolicyGateImpact {
  count: number
}

/** `POST /api/settings/policy-gate/remove-unaccepted` and `.../enforce`. */
interface PolicyGateRemovalResult {
  removed: string[]
  failed: Array<{ email: string; error: string }>
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

export function SettingsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [excluded, setExcluded] = useState<string[]>([])
  const [hideCosts, setHideCosts] = useState(false)
  const [contestFloor, setContestFloor] = useState(3)
  const [defaultTheme, setDefaultTheme] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Round 14, Unit 12: the policy gate switch. Kept SEPARATE from the
  // hideCosts/contestFloor/excludedUsers state above and their shared
  // "Save changes" button on purpose -- turning this on is a distinct
  // ACTION with a real consequence (it removes people from CHU-GPT-Users),
  // never a field that rides along with an unrelated leaderboard edit.
  const [policyGateEnforced, setPolicyGateEnforced] = useState(false)

  // Chubacca on CHU GPT's new-chat screen (Maroun, 24 Sep 2026). Its own
  // store (the chat-settings secret the chat app reads) and its own save:
  // a flip is saved at once, never held for the leaderboard's button.
  const [chatMascot, setChatMascot] = useState(false)
  const [chatMascotSound, setChatMascotSound] = useState(true)
  const [chatLoading, setChatLoading] = useState(true)
  const [chatSaving, setChatSaving] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)
  const [chatSaved, setChatSaved] = useState(false)
  const [policyGateBusy, setPolicyGateBusy] = useState(false)
  const [policyGateError, setPolicyGateError] = useState<string | null>(null)
  const [policyGateResult, setPolicyGateResult] = useState<string | null>(null)
  /** The live "who would this affect" count, fetched on demand -- never
   *  guessed or cached across a page sit, because the whole point is that
   *  it can only ever be trusted at the moment an action is about to act
   *  on it. `null` means "not fetched yet"; the confirm panel below fetches
   *  it the moment it opens. */
  const [policyGateImpact, setPolicyGateImpact] = useState<number | null>(null)
  /**
   * Which confirm panel (if any) is open -- 'enable' for turning the gate
   * on, 'remove' for the standing "remove today's non-accepters" action
   * while already on (decision d). ONE panel shape, two modes, because both
   * are the SAME destructive action (removing people from CHU-GPT-Users)
   * and both need the same live count in front of an admin before they can
   * click through it. Turning OFF has no mode here at all: decision c
   * states plainly that OFF removes nobody, so it needs no confirmation.
   */
  const [policyGateConfirming, setPolicyGateConfirming] = useState<'enable' | 'remove' | null>(null)

  const [admins, setAdmins] = useState<AdminHolder[]>([])
  const [adminsError, setAdminsError] = useState<string | null>(null)

  const [diagnostics, setDiagnostics] = useState<DiagnosticsReport | null>(null)
  const [diagnosticsRunning, setDiagnosticsRunning] = useState(false)
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null)

  /**
   * The self-test is a BUTTON, not an auto-load: it exercises every read
   * path the panel has, including the gateway and both databases, so firing
   * it on every visit to Settings would be a lot of work nobody asked for.
   */
  async function runDiagnostics() {
    setDiagnosticsRunning(true)
    setDiagnosticsError(null)
    try {
      setDiagnostics(await apiJson<DiagnosticsReport>('/api/diagnostics'))
    } catch (err) {
      setDiagnostics(null)
      setDiagnosticsError(errorMessage(err))
    } finally {
      setDiagnosticsRunning(false)
    }
  }
  const [adminBusy, setAdminBusy] = useState<string | null>(null)

  const [assignments, setAssignments] = useState<AppRoleAssignment[]>([])
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null)

  const loadAssignments = useCallback(async () => {
    setAssignmentsError(null)
    try {
      const data = await apiJson<{ assignments: AppRoleAssignment[] }>('/api/users/app-roles')
      setAssignments(data.assignments ?? [])
    } catch (err) {
      setAssignmentsError(errorMessage(err))
    }
  }, [])

  const loadAdmins = useCallback(async () => {
    setAdminsError(null)
    try {
      const data = await apiJson<{ admins: AdminHolder[] }>('/api/users/admins')
      setAdmins(data.admins)
    } catch (err) {
      setAdminsError(errorMessage(err))
    }
  }, [])

  async function grantAdmin(user: DirectoryUser) {
    const email = dirEmail(user)
    if (!email) {
      return
    }
    setAdminBusy(user.id)
    setAdminsError(null)
    try {
      await apiJson('/api/users/admin-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, grant: true }),
      })
      await loadAdmins()
    } catch (err) {
      setAdminsError(errorMessage(err))
    } finally {
      setAdminBusy(null)
    }
  }

  async function revokeAdmin(holder: AdminHolder) {
    const confirmed = window.confirm(`Revoke the Admin role from ${holder.name}? They lose access to the admin panel.`)
    if (!confirmed) {
      return
    }
    setAdminBusy(holder.id)
    setAdminsError(null)
    try {
      await apiJson('/api/users/admin-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: holder.id, grant: false }),
      })
      await loadAdmins()
    } catch (err) {
      setAdminsError(errorMessage(err))
    } finally {
      setAdminBusy(null)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiJson<{ settings: LeaderboardSettings }>('/api/settings/leaderboard')
      setExcluded(data.settings.excludedUsers)
      setHideCosts(data.settings.hideCosts)
      setContestFloor(data.settings.contestFloor ?? 3)
      setDefaultTheme(data.settings.defaultTheme ?? '')
      setPolicyGateEnforced(data.settings.policyGateEnforced)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadChat = useCallback(async () => {
    setChatLoading(true)
    setChatError(null)
    try {
      const data = await apiJson<{ settings: { chatMascot: boolean; chatMascotSound?: boolean } }>('/api/settings/chat')
      setChatMascot(data.settings.chatMascot)
      setChatMascotSound(data.settings.chatMascotSound !== false)
    } catch (err) {
      setChatError(`Could not load the chat setting: ${errorMessage(err)}`)
    } finally {
      setChatLoading(false)
    }
  }, [])

  /** Saves one switch; the server keeps the other as stored. */
  async function saveChat(change: { chatMascot: boolean } | { chatMascotSound: boolean }) {
    setChatSaving(true)
    setChatError(null)
    setChatSaved(false)
    try {
      const data = await apiJson<{ settings: { chatMascot: boolean; chatMascotSound?: boolean } }>('/api/settings/chat', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(change),
      })
      setChatMascot(data.settings.chatMascot)
      setChatMascotSound(data.settings.chatMascotSound !== false)
      setChatSaved(true)
    } catch (err) {
      setChatError(`Could not save the chat setting: ${errorMessage(err)}`)
    } finally {
      setChatSaving(false)
    }
  }

  useEffect(() => {
    void load()
    void loadChat()
    void loadAdmins()
    void loadAssignments()
  }, [load, loadChat, loadAdmins, loadAssignments])

  /** Re-fetched every time it is needed, never cached across a page sit --
   *  see policyGateImpact's own doc comment. Used both by the "turn on"
   *  confirm panel and by the standing widget shown while already
   *  enforced. */
  const loadPolicyGateImpact = useCallback(async () => {
    setPolicyGateError(null)
    try {
      const data = await apiJson<PolicyGateImpact>('/api/settings/policy-gate/impact')
      setPolicyGateImpact(data.count)
    } catch (err) {
      setPolicyGateImpact(null)
      setPolicyGateError(errorMessage(err))
    }
  }, [])

  useEffect(() => {
    // The standing "N people accepted an older version" widget (decision d)
    // needs a current count for as long as the gate IS enforced, refreshed
    // on load like every other setting here -- not fetched at all while
    // off, since there is nothing to show.
    if (policyGateEnforced) void loadPolicyGateImpact()
  }, [policyGateEnforced, loadPolicyGateImpact])

  function openPolicyGateConfirm(mode: 'enable' | 'remove') {
    setPolicyGateConfirming(mode)
    setPolicyGateResult(null)
    void loadPolicyGateImpact()
  }

  function summariseRemoval(result: PolicyGateRemovalResult): string {
    return result.failed.length
      ? `Removed ${result.removed.length}. Failed: ${result.failed.map((f) => `${f.email} (${f.error})`).join(', ')}`
      : `Removed ${result.removed.length} ${result.removed.length === 1 ? 'person' : 'people'}.`
  }

  /** The ONLY path that turns the gate on. Removes today's non-accepters
   *  first, then flips the switch -- one server-side action
   *  (`POST /api/settings/policy-gate/enforce`), so the flag can never read
   *  "enforced" while the removal it implies has not actually happened. */
  async function confirmEnablePolicyGate() {
    setPolicyGateBusy(true)
    setPolicyGateError(null)
    try {
      const result = await apiJson<PolicyGateRemovalResult & { settings: LeaderboardSettings }>(
        '/api/settings/policy-gate/enforce',
        { method: 'POST' },
      )
      setPolicyGateEnforced(result.settings.policyGateEnforced)
      setPolicyGateResult(summariseRemoval(result))
      setPolicyGateConfirming(null)
      await loadPolicyGateImpact()
    } catch (err) {
      setPolicyGateError(errorMessage(err))
    } finally {
      setPolicyGateBusy(false)
    }
  }

  /** Turning the gate OFF removes nobody (decision c, stated explicitly) --
   *  a plain field write through the same route every other setting here
   *  uses, needing no confirm. */
  async function disablePolicyGate() {
    setPolicyGateBusy(true)
    setPolicyGateError(null)
    try {
      await apiJson('/api/settings/leaderboard', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          excludedUsers: excluded,
          hideCosts,
          contestFloor,
          policyGateEnforced: false,
          ...(defaultTheme ? { defaultTheme } : {}),
        }),
      })
      setPolicyGateEnforced(false)
      setPolicyGateResult(null)
    } catch (err) {
      setPolicyGateError(errorMessage(err))
    } finally {
      setPolicyGateBusy(false)
    }
  }

  /** The standing removal action (decision d): available any time the gate
   *  is already enforced, for whoever the LATEST policy-version bump has
   *  left behind -- never automatic, always this same click. */
  async function removeUnaccepted() {
    setPolicyGateBusy(true)
    setPolicyGateError(null)
    try {
      const result = await apiJson<PolicyGateRemovalResult>('/api/settings/policy-gate/remove-unaccepted', {
        method: 'POST',
      })
      setPolicyGateResult(summariseRemoval(result))
      setPolicyGateConfirming(null)
      await loadPolicyGateImpact()
    } catch (err) {
      setPolicyGateError(errorMessage(err))
    } finally {
      setPolicyGateBusy(false)
    }
  }

  function addExcluded(user: DirectoryUser) {
    const email = dirEmail(user).toLowerCase()
    if (email.length > 0 && !excluded.includes(email)) {
      setExcluded((prev) => [...prev, email])
      setSaved(false)
    }
  }

  function removeEmail(email: string) {
    setExcluded((prev) => prev.filter((e) => e !== email))
    setSaved(false)
  }

  async function save() {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await apiJson('/api/settings/leaderboard', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // Empty string means "no org default": send nothing rather than an
        // empty id, so the stored secret keeps its pre-theme shape.
        body: JSON.stringify({
          excludedUsers: excluded,
          hideCosts,
          contestFloor,
          // The backend 400s a false-to-true flip through this route on
          // purpose (Unit 12, decision c) -- sending the CURRENT value back
          // unchanged is what keeps this button from ever attempting one by
          // accident; turning the gate on has its own dedicated action
          // below, with its own confirm.
          policyGateEnforced,
          ...(defaultTheme ? { defaultTheme } : {}),
        }),
      })
      setSaved(true)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.25rem 0.3rem 0.25rem 0.7rem',
    borderRadius: '9999px',
    background: tokens.surfaceRaised,
    border: `1px solid ${tokens.border}`,
    fontSize: '0.82rem',
    color: tokens.textPrimary,
  }

  return (
    <div style={styles.page}>
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Leaderboard settings</h2>
          <button type="button" style={styles.button} onClick={() => void save()} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Controls the staff arcade leaderboard at arcade.chugpt.apps.chu.com.au. Changes apply within a minute.
        </p>
        {error ? <p style={styles.error}>{error}</p> : null}
        {saved ? <p style={{ ...styles.hint, color: tokens.brand }}>Saved.</p> : null}

        {loading ? (
          <p style={styles.hint}>Loading settings…</p>
        ) : (
          <>
            <div style={styles.field}>
              <label style={styles.checkboxRow}>
                <input type="checkbox" checked={hideCosts} onChange={(e) => { setHideCosts(e.target.checked); setSaved(false) }} disabled={saving} />
                Hide costs on the leaderboard
              </label>
              <span style={styles.hint}>
                When on, dollar figures are withheld from the board entirely and shown pixelated. The board runs on
                tokens, levels, and streaks instead.
              </span>
            </div>

            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="arcade-contest-floor">
                Contest floor
              </label>
              <span style={styles.hint}>
                How many active people a group needs to be fielded as a side in Office of Origin / Team Wars, and to
                move the shield. This is a FAIRNESS rule, not a privacy one: every office and every team is shown on
                the board at every value, with its active count printed. What the number changes is who can be played
                as a side, because a one-person side&rsquo;s score is one person&rsquo;s week and making that a
                company-wide scoreline turns that person into the reason their side lost.
              </span>
              <input
                id="arcade-contest-floor"
                type="number"
                min={1}
                max={10}
                value={contestFloor}
                onChange={(e) => {
                  setContestFloor(Number(e.target.value))
                  setSaved(false)
                }}
                disabled={saving}
                style={{ ...styles.input, maxWidth: 120 }}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="arcade-default-theme">
                Arcade default theme
              </label>
              <span style={styles.hint}>
                The skin the staff arcade opens on for anyone who has not chosen one. A viewer's own choice is kept in
                their browser and always wins. Presentation only: a theme cannot change what the board shows, and
                hide-costs is enforced on the server regardless of skin.
              </span>
              <select
                id="arcade-default-theme"
                value={defaultTheme}
                onChange={(e) => {
                  setDefaultTheme(e.target.value)
                  setSaved(false)
                }}
                disabled={saving}
                style={{ ...styles.input, marginTop: '0.5rem', maxWidth: '22rem' }}
              >
                <option value="">Neon Cabinet (built-in default)</option>
                {ARCADE_THEMES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.tagline}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <span style={styles.fieldLabel}>Excluded accounts</span>
              <span style={styles.hint}>
                Emails hidden from the board and team totals, on top of the built-in service accounts. Use this for
                admin or service identities that should not appear as players.
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.6rem 0' }}>
                {excluded.length === 0 ? (
                  <span style={styles.hint}>None yet.</span>
                ) : (
                  excluded.map((email) => (
                    <span key={email} style={pill}>
                      {email}
                      <button
                        type="button"
                        aria-label={`Remove ${email}`}
                        style={{ ...styles.buttonGhost, padding: '0 0.4rem', fontSize: '0.9rem', border: 'none' }}
                        onClick={() => removeEmail(email)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
              <DirectoryPicker
                tokens={tokens}
                onPick={addExcluded}
                placeholder="Search the directory to exclude someone…"
                disabled={saving}
              />
            </div>
          </>
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>CHU GPT chat</h2>
        </div>
        <p style={styles.sectionSubtitle}>
          Controls the chat at chugpt.apps.chu.com.au. Saved as soon as you flip it; open chats pick it up within a
          minute.
        </p>
        {chatError ? <p style={styles.error}>{chatError}</p> : null}
        <div style={styles.field}>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={chatMascot}
              onChange={(e) => void saveChat({ chatMascot: e.target.checked })}
              disabled={chatLoading || chatSaving}
            />
            Chubacca on new chats
          </label>
          <span style={styles.hint}>
            Chubacca sits on the message box of a new, empty chat and leaves when the first message is sent. Clicking
            him plays another clip, with sound.
          </span>
        </div>
        <div style={styles.field}>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={chatMascotSound}
              onChange={(e) => void saveChat({ chatMascotSound: e.target.checked })}
              disabled={chatLoading || chatSaving}
            />
            Chubacca&rsquo;s sound
          </label>
          <span style={styles.hint}>
            In the chat and the staff portal. When off, clicking him still plays another clip, silently.
          </span>
          {chatSaved ? (
            <span style={{ ...styles.hint, color: tokens.brand }}>
              {chatMascot ? 'Saved. Chubacca is on for new chats.' : 'Saved. Chubacca is off.'}
            </span>
          ) : null}
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Policy gate</h2>
          <span
            style={{
              ...pill,
              background: policyGateEnforced ? tokens.brand : tokens.surfaceRaised,
              color: policyGateEnforced ? '#FFFFFF' : tokens.textPrimary,
            }}
          >
            {policyGateEnforced ? 'Enforced' : 'Off'}
          </span>
        </div>
        <p style={styles.sectionSubtitle}>
          Whether CHU-GPT-Users membership requires accepting the current AI usage policy. This is the ONLY
          enforcement location: chat (LibreChat&rsquo;s own Entra app assignment), Teams and the API all check the
          same group. The acceptance flow itself runs regardless of this switch, so people can read and accept the
          policy before it is turned on. Admin role holders are not exempt from accepting, but this console never
          depends on CHU-GPT-Users itself, so nobody can lock themselves out of the panel by turning this on.
        </p>
        {policyGateError ? <p style={styles.error}>{policyGateError}</p> : null}
        {policyGateResult ? <p style={{ ...styles.hint, color: tokens.brand }}>{policyGateResult}</p> : null}

        {!policyGateEnforced ? (
          <button
            type="button"
            style={styles.button}
            onClick={() => openPolicyGateConfirm('enable')}
            disabled={policyGateBusy || loading}
          >
            Turn on
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            <button type="button" style={styles.buttonGhost} onClick={() => void disablePolicyGate()} disabled={policyGateBusy}>
              Turn off
            </button>
            <div style={styles.field}>
              <span style={styles.fieldLabel}>People without the current version accepted</span>
              <span style={styles.hint}>
                Never automatic. A policy version bump (docs/guides/policy.md) can leave people behind on an older
                acceptance at any time while this is on; this count and this action are always available, and
                nothing is removed until you click through the confirm below.
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '1.4rem', fontWeight: 700, color: tokens.textPrimary }}>
                  {policyGateImpact === null ? '…' : policyGateImpact}
                </span>
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={() => openPolicyGateConfirm('remove')}
                  disabled={policyGateBusy || policyGateImpact === 0}
                >
                  Remove them from CHU-GPT-Users
                </button>
              </div>
            </div>
          </div>
        )}

        {policyGateConfirming ? (
          <div
            role="alertdialog"
            aria-label={policyGateConfirming === 'enable' ? 'Confirm turning on the policy gate' : 'Confirm removal'}
            style={{
              marginTop: '1rem',
              padding: '1rem 1.2rem',
              borderRadius: '0.8rem',
              border: `1px solid ${tokens.danger}`,
              background: tokens.surfaceRaised,
            }}
          >
            <p style={{ margin: '0 0 0.6rem', color: tokens.textPrimary, fontWeight: 600 }}>
              {policyGateConfirming === 'enable'
                ? 'Turning this on removes people immediately.'
                : 'This removes people immediately.'}
            </p>
            <p style={{ margin: '0 0 0.8rem', color: tokens.textSecondary }}>
              {policyGateImpact === null
                ? 'Checking who this affects…'
                : `${policyGateImpact} ${policyGateImpact === 1 ? 'person' : 'people'} in CHU-GPT-Users with no acceptance of the current policy version will be removed from that group. Each removal is its own audit row.`}
            </p>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                type="button"
                style={styles.button}
                onClick={() => void (policyGateConfirming === 'enable' ? confirmEnablePolicyGate() : removeUnaccepted())}
                disabled={policyGateBusy || policyGateImpact === null}
              >
                {policyGateBusy
                  ? 'Working…'
                  : policyGateConfirming === 'enable'
                    ? `Yes, remove ${policyGateImpact ?? '…'} and turn on`
                    : `Yes, remove ${policyGateImpact ?? '…'}`}
              </button>
              <button
                type="button"
                style={styles.buttonGhost}
                onClick={() => setPolicyGateConfirming(null)}
                disabled={policyGateBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <ModelAccessSwitch tokens={tokens} />

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Admin access</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadAdmins()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Who holds the Admin app role (full access to this panel). Admin is meant to come from the
          <strong> CHU-GPT-Admin</strong> group, which appears below as a group rather than as a person; direct
          per-user assignments are exceptions worth knowing about, and those are the ones that carry a Revoke.
          Changes take effect on the person's next sign-in.
        </p>
        {adminsError ? <p style={styles.error}>{adminsError}</p> : null}
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Grant Admin to</span>
          <DirectoryPicker
            tokens={tokens}
            onPick={(u) => void grantAdmin(u)}
            placeholder="Search the directory to grant Admin…"
            excludeIds={admins.map((a) => a.id)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {admins.length === 0 ? (
            <span style={styles.hint}>No admins listed yet.</span>
          ) : (
            admins.map((a) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.6rem',
                  padding: '0.5rem 0.8rem',
                  borderRadius: '0.6rem',
                  border: `1px solid ${tokens.border}`,
                  background: tokens.surfaceRaised,
                }}
              >
                <span style={{ color: tokens.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {a.name}
                  {a.isGroup ? (
                    <span
                      style={{
                        ...styles.badge,
                        background: tokens.surfaceRaised,
                        color: tokens.textSecondary,
                        border: `1px solid ${tokens.border}`,
                      }}
                    >
                      Group
                    </span>
                  ) : null}
                </span>
                {/* A group is how Admin is MEANT to be held, so its row does
                    not offer a Revoke. Membership is the thing to change, in
                    Entra, and pretending otherwise is what produced a button
                    that silently failed. */}
                {a.isGroup ? (
                  <span style={styles.hint}>
                    Admin comes from this group by design — change its membership in Entra, not here.
                  </span>
                ) : (
                  <button
                    type="button"
                    style={styles.buttonDanger}
                    disabled={adminBusy === a.id}
                    onClick={() => void revokeAdmin(a)}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>App role assignments</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadAssignments()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Every app-role assignment on the CHU-GPT app registration, User and Admin alike. The
          section above answers "who can open this panel"; this one answers "who holds a role on the
          platform app, and how". By design the sign-in gate is granted to the CHU-GPT-Users GROUP
          and Admin comes via CHU-GPT-Admin, so a direct per-user assignment here is an exception
          worth knowing about rather than a normal state.
        </p>
        {assignmentsError ? <p style={styles.error}>{assignmentsError}</p> : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {assignments.length === 0 && !assignmentsError ? (
            <span style={styles.hint}>No assignments listed.</span>
          ) : (
            assignments
              // Admin first, then group assignments, then by name: the two
              // things an operator scans for are at the top either way.
              .slice()
              .sort(
                (a, b) =>
                  a.roleName.localeCompare(b.roleName) ||
                  Number(b.isGroup) - Number(a.isGroup) ||
                  (a.principalDisplayName ?? '').localeCompare(b.principalDisplayName ?? ''),
              )
              .map((assignment) => (
                <div
                  key={assignment.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.6rem',
                    padding: '0.45rem 0.8rem',
                    borderRadius: '0.6rem',
                    border: `1px solid ${tokens.border}`,
                    background: tokens.surfaceRaised,
                  }}
                >
                  <span style={{ color: tokens.textPrimary }}>
                    {assignment.principalDisplayName ?? assignment.principalId}{' '}
                    <span style={styles.hint}>{assignment.isGroup ? 'group' : 'user'}</span>
                  </span>
                  <span style={styles.badge}>{assignment.roleName}</span>
                </div>
              ))
          )}
        </div>
      </section>

      {/*
        Diagnostics. "Do these functions work" used to be answerable only by
        reading the code and checking IAM by hand, which is how the
        drift-detector permission gap sat behind a green tick for days and
        how the ListServices truncation survived the life of the Overview.
        This turns that question into a button, and every probe prints what
        it actually READ rather than a bare pass.
      */}
      <section style={styles.section} aria-label="Diagnostics">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Diagnostics</h2>
          <button
            type="button"
            style={styles.button}
            onClick={() => void runDiagnostics()}
            disabled={diagnosticsRunning}
          >
            {diagnosticsRunning ? 'Running…' : 'Run self-test'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Every read path the panel depends on, tested live and read-only. A probe that could not run reports an
          error, never a pass, and each one prints the count it read against the count it expected — so a truncation
          or a silently missing grant is visible here rather than behind a green tick.
        </p>
        {diagnosticsError ? <p style={styles.error}>{diagnosticsError}</p> : null}
        {diagnostics ? (
          <>
            <p
              style={{
                ...styles.hint,
                color: diagnostics.overall === 'ok' ? tokens.brand : tokens.danger,
                fontWeight: 700,
              }}
            >
              {diagnostics.overall === 'ok'
                ? 'All read paths green.'
                : diagnostics.overall === 'warn'
                  ? 'Green apart from warnings below.'
                  : 'One or more read paths are BROKEN.'}{' '}
              Run at {new Date(diagnostics.ranAt).toLocaleTimeString('en-AU')}.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {diagnostics.probes.map((probe) => {
                const colour =
                  probe.status === 'ok'
                    ? tokens.brand
                    : probe.status === 'skipped'
                      ? tokens.border
                      : tokens.danger
                return (
                  <div
                    key={probe.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.8rem',
                      flexWrap: 'wrap',
                      border: `1px solid ${tokens.border}`,
                      borderLeft: `3px solid ${colour}`,
                      borderRadius: '0.6rem',
                      background: tokens.surfaceRaised,
                      padding: '0.55rem 0.9rem',
                    }}
                  >
                    <span style={{ color: tokens.textPrimary, fontWeight: 600, minWidth: '14rem' }}>
                      {probe.status === 'ok'
                        ? '[ok]'
                        : probe.status === 'skipped'
                          ? '[--]'
                          : probe.status === 'warn'
                            ? '[!]'
                            : '[!!]'}{' '}
                      {probe.label}
                    </span>
                    <span style={{ ...styles.hint, flex: 1, color: probe.status === 'error' ? tokens.danger : tokens.textSecondary }}>
                      {probe.detail}
                    </span>
                    <span style={styles.hint}>{probe.ms} ms</span>
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <p style={styles.hint}>Not run yet.</p>
        )}
      </section>
    </div>
  )
}

export default SettingsPage
